import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Box, Button, DialogActions, DialogContent, 
    Link, DialogProps, TextField, Typography, 
    FormControl,
    Select,
    InputLabel,
    MenuItem } from '@mui/material';
import { Client, Patient as OutreachPatient, PatientCollection, PatientTrackables, TrackableMeasureTypes, Visit } from '../../models/core';
import { API as ZiphyAPI } from '../../utils/ZiphyAPI';
import { useQuery } from '@tanstack/react-query';
import { Patient as ZiphyPatient, Place, DTime, ScheduleStaffPair, Service, Address } from '../../models/ziphy';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime, IANAZone, Zone } from 'luxon';
import ZiphyPatientInput from '../book/ZiphyPatientInput';
import ZiphyPlaceInput from '../book/ZiphyPlaceInput';
import SpinnerButton from '../SpinnerButton';
import { luxonToDtime } from '../../utils/time';
import AddressDialog, { AddressDialogMode } from '../patients/2_0/schedule/AddressDialog';
import useDialog from '../../hooks/useDialog';
import GridForm, { GridFormItem } from '../GridForm';
import CalendarGrid from '../patients/2_0/schedule/CalendarGrid';
import ConfirmBookingDialog from '../patients/2_0/schedule/ConfirmBookingDialog';
import { API } from '../../utils/Api';
import SimpleDialog from '../dialogs/SimpleDialog';
import { MeasureEntryList, VisitReasons } from '../../utils/enums';
import ResizableDialog from '../dialogs/ResizableDialog';


function useServicesQuery(practiceId: number) {
    return useQuery({
        queryKey: ['practice_services', practiceId],
        queryFn: async () => (await ZiphyAPI.getPracticeServices(practiceId)),
        select: ({ practice_services, expanded: { services: { items: serviceMap } } }) => practice_services.items.map(ps => ({ ...ps, service: serviceMap.find(it => it.key == ps.service_id)?.value })),
        staleTime:60_000
    });
}

function getAddress(outreachPatient:OutreachPatient):Address {
    return {
        id:'',
        line:[outreachPatient.address,outreachPatient.address2],
        postalCode:outreachPatient.zip,
        state:outreachPatient.state,
        city:outreachPatient.city
    } as Address
}

function useOfficeQuery(practiceId: number) {
    return useQuery({
        queryKey: ['practice_office', practiceId],
        queryFn: async () => (await ZiphyAPI.getOffices({ 
            and:[
                { eq: ['is_active', true] },
                { eq: ['practice_id', practiceId] }
            ]
        })),
        select: (data) => data?.offices?.items || [],
        staleTime:120_000
    });
}

function usePracticeQuery(practiceId: number) {
    return useQuery({
        queryKey: ['practice_read', practiceId],
        queryFn: async () => (await ZiphyAPI.getPractice(practiceId)),
        select: (data) => data,
        staleTime:120_000
    });
}

function useTrackableQuery(patientId: string, clientId:string) {
    return useQuery({
        queryKey: ['patient_trackables', patientId, clientId],
        queryFn: async () => (await API.getPatientTrackables(clientId, patientId)),
        select: (data) => data || [],
        staleTime:30_000
    });
}

interface Props extends DialogProps {
    open: boolean;
    outreachPatient:OutreachPatient;
    outreachClient:Client;
    outreachVisit?:Visit;
    practiceId: number;
    date: DateTime;
    onSubmit?: () => void;
    onUpdate?: (collection:PatientCollection) => void;
    onCancel?: () => void;
}

export interface BookingWindow {
    id:number;
    time:DTime;
    localTime:DateTime;
    utcTime:DateTime;
    staff:number[];
    hasPrefered:boolean;
}

const BookDialog = ({ open, outreachPatient:patient1, outreachVisit:visit1, outreachClient,
    practiceId, date:initialDate, onUpdate, onCancel, ...props  }: Props): JSX.Element => {

    const [ziphyPatient, setZiphyPatient] = useState<ZiphyPatient | null>(null);
    const [place, setPlace] = useState<Place | null>(null);
    const [startDate, setStartDate] = useState<DateTime>((initialDate || DateTime.now()).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }));
    const [endDate, setEndDate] = useState<DateTime>((initialDate || DateTime.now()).set({ day:(DateTime.now() || initialDate).day + 1, hour: 0, minute: 0, second: 0, millisecond: 0 }));
    const [serviceId, setServiceId] = useState('');
    const [outreachPatient, _setOutreachPatient] = useState<OutreachPatient>(patient1 || {} as OutreachPatient);
    const [outreachVisit, _setOutreachVisit] = useState<Visit>(visit1 || {} as Visit);
    const [patientNotes, setPatientNotes] = useState<string>('');
    
    const [scheduleStaff, setScheduleStaff] = useState<ScheduleStaffPair[]>();
    const [scheduleWindows, setScheduleWindows] = useState<BookingWindow[]>();
    const [selectedWindow, setSelectedWindow] = useState<BookingWindow>();
    const [providers, setProviders] = useState<ScheduleStaffPair[]>();
    const [agents, setAgents] = useState<ScheduleStaffPair[]>();
    const [providerId, setProviderId] = useState<number>();
    const [agentId, setAgentId] = useState<number>();
    const [useInsurance, setUseInsurance] = useState<boolean>(true);
    const [searchingSchedules, setSearchingSchedules] = useState(false);

    const { show: showPlaceEditor, hide: hidePlaceEditor, open: isPlaceEditorOpen } = useDialog();
    const { show: showBookingError, hide: hideBookingError, open: isBookingErrorOpen } = useDialog();
    const { show: showBookingSuccess, hide: hideBookingSuccess, open: isBookingSuccesOpen } = useDialog();


    const servicesQuery = useServicesQuery(practiceId);
    const service = useMemo(() => (
        servicesQuery.data?.find(s => s.service_id + '' == serviceId)
    ), [servicesQuery, serviceId]);

    const officeQuery = useOfficeQuery(practiceId);
    const office = useMemo(() => {
        return officeQuery?.data?.find((o) => o.is_active && o.is_main);
    },[officeQuery]);

    const officeZone = useMemo(() => {
        return IANAZone.create(office?.timezone || 'UTC');
    },[office])

    const practiceQuery = usePracticeQuery(practiceId);
    const patientTrackables = useTrackableQuery(outreachPatient._id, outreachClient._id);

    const onEnumChange = useCallback((label: string, value: string | null) => {
        if(label == 'service') {
            setServiceId(value || '');
        }
    }, []);

    const onStartDateChange = useCallback((value: string|null) => {
        if(!value) { return; }
        setStartDate(
            DateTime.fromISO(value).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        );
        setEndDate(
            DateTime.fromISO(value).plus({days:1}).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        );
    }, []);

    const onEndDateChange = useCallback((value: string|null) => {
        if(!value) { return; }
        setEndDate(
            DateTime.fromISO(value).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        );
    }, []);

    const buildServiceSelectOptions = useMemo(() => {

        const capitalizeFirstLetter = (str: string) => 
            str.charAt(0).toUpperCase() + str.slice(1);

        const servArr = servicesQuery.data || [];
        const serviceRecord: Record<string, [string, string][]> = {};
    
        servArr
            .filter((ps) => !ps.service?.has_symptoms)
            .forEach((s) => {
                const key = s?.service?.patient_group_type || "";
                if (!serviceRecord[key]) {
                    serviceRecord[key] = [];
                }
    
                serviceRecord[key].push([
                    s.service_id + "",
                    `${s.service?.name} (${s.duration}min, ` +
                    [
                        ...(s.service?.agent_load ? ["OCC"] : []),
                        ...(s.service?.provider_load ? ["Provider"] : []),
                    ].join("+") + ")",
                ]);
            });
    
        // Convert serviceRecord into Select options with headers
        const selectOptions = Object.entries(serviceRecord).flatMap(([header, items]) => [
            { type: "header", value:'', label: capitalizeFirstLetter(header) },
            ...items.map(([value, label]) => ({ type: "option", value:value, label })),
        ]);
    
        return selectOptions;
    }, [servicesQuery]);


    const createPatientNotes = React.useCallback((pT:PatientTrackables[], outreachPatient:OutreachPatient, visit?:Visit) => {
        function capitalizeFirstLetter(str: string): string {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        }
        function copyConditionData(contentName:string, content:{name:string, value:string}[], skipValue:boolean) {
            let copyData:string = capitalizeFirstLetter(contentName) + ':';
            content?.forEach((it) => { copyData = copyData + `\n${it.name}${skipValue ? '' : ':' + it.value}`; });
            return copyData;
        }
        function copyMeasureData(contentName:string, content:{name:string, value:string}[]) {
            const measureLookup = MeasureEntryList.reduce((acc, entry) => {
            acc[entry.code] = entry.name;
            return acc;
            }, {} as Record<string, string>);

            let copyData:string = capitalizeFirstLetter(contentName) + ':';
            content?.forEach((it) => { copyData = copyData + `\n${ measureLookup[it.name] ? measureLookup[it.name] : it.name}`; });
            return copyData;
        }

        
        const vrMapping =  visit && visit.visitReasons && visit.visitReasons.length != 0 
            ? visit.visitReasons.filter(vr => VisitReasons.find(t => t.code == vr)) : [];
        const visitReasons = vrMapping.map(e => VisitReasons.find(t => t.code == e)?.name)?.join('\n') || '';

        let copyString = [
            `Name: ${outreachPatient?.lastName}, ${outreachPatient?.firstName}`,
            ...(outreachPatient?.dob ? [`DOB: ${outreachPatient.dob}`] : []),
            ...(outreachPatient?.gender ? [`SAB: ${outreachPatient.gender.toString()}`] : []),
            ...(outreachPatient?.language ? [`Language: ${outreachPatient.language}`] : []),
            ...(outreachPatient?.phone ? [`Phone: ${outreachPatient.phone}`] : []),
            ...(visitReasons.trim() !== '' ? [`Reasons:\n${visitReasons}`] : [] ),        
        ].join('\n');
        
        copyString += '\n';
        let measureString = '';
        const conditionsList:Record<string, {name:string, value:string}[]> = {};
        conditionsList[TrackableMeasureTypes.MEASURE] = [];
        conditionsList[TrackableMeasureTypes.SUSPECT] = [];
        conditionsList[TrackableMeasureTypes.CHRONIC] = [];
        conditionsList[TrackableMeasureTypes.DIAGNOSIS] = [];

        pT?.forEach((it) => {
            it?.trackable?.forEach((item) => {
                if(!conditionsList[item.type]) {    conditionsList[item.type] = []; }
                if(!conditionsList[item.type].find(ci => ci.name == item.name)) {
                    conditionsList[item.type].push({ name:item.name || '', value:item.value || '' })
                }
            })
        });

        Object.entries(conditionsList).forEach((e) => {
            if(e[1].length != 0) {
                measureString += e[0] == TrackableMeasureTypes.MEASURE
                ? copyMeasureData(e[0],e[1]) + '\n'
                : copyConditionData(e[0],e[1], false) + '\n'
            }
        });
        return copyString + (measureString.length != 0 ? '\n' + measureString : '');
    
    }, []);

    const onConfirmDialogUpdateState = useCallback((provider:number, agent:number, insurance:boolean, notes:string) => {
        setProviderId(provider);
        setAgentId(agent);
        setUseInsurance(insurance);
        setPatientNotes(notes);
    },[])

    const requestStaffSchedule = useCallback(async (patient:ZiphyPatient, zone:Zone, begin:DTime, end:DTime, 
        officeId:number, service:Service, placeId:number, patientNumber = 1) => {
            try {
                const needsProvider = service.provider_load != undefined && service.provider_load != null;
                const needsAgent = service.agent_load != undefined && service.agent_load != null;

                const request = await ZiphyAPI.getSchedulableStaff(begin, end, officeId, true, placeId, service.id, patientNumber);
                if(request) {
                    const patPref = (patient.preferred_provider_ids || []);
                    const staffMap = request?.staff?.items?.items || [];
                    const providers = staffMap.filter(s => s.value?.role == 'provider' && s.key != undefined)?.map(m => m.key) || [];
                    const agents = staffMap.filter(s => s.value?.role == 'agent' && s.key != undefined)?.map(m => m.key) || [];
                    const mapping = (request?.windows?.items?.items || [])
                        .filter(ft => {
                            return ft?.key != undefined
                                && (!needsProvider ? true : providers.find(p => (ft.value || []).includes(p||0)))
                                && (!needsAgent ? true : agents.find(p => (ft.value || []).includes(p||0)));
                            }
                        )
                        .map((its, idx) => {
                            const time = its.key || {} as DTime;
                            const val = its.value||[];
                            const utcDateTime = DateTime.utc(time.year, time.month, time.day, time.hour, time.minute, 0);
                            const t = {
                                id:idx,
                                time:time,
                                localTime:utcDateTime.setZone(zone),
                                utcTime:utcDateTime,
                                staff:val,
                                hasPrefered:!!patPref.find(pr => val.includes(pr))
                            }
                            return t;
                    });
                    setScheduleWindows(mapping);
                    setScheduleStaff(request?.staff?.items?.items || []);
                }
            }catch(error) {
                console.error(error);
                setScheduleWindows([]);
                setScheduleStaff([]);
            }
    },[]);

    const onBookClicked = useCallback(async (patientId:number, practiceId:number, officeId:number, placeId:number, serviceId:number,
        agentId:number, providerId:number, notes:string, time:DTime, insurance:boolean
    ) => {
        try {
            const request = await ZiphyAPI.bookAppointment(
                {
                    practice_id:practiceId,
                    office_id:officeId,
                    place_id:placeId,
                    service_id:serviceId,
                    agent_role_id:agentId,
                    provider_role_id:providerId,
                    description: notes,
                    answers: { 0: { 1: 1, 2: 1, 7: 1, 8: 1, 9: 1, 10: 1 } },
                    start: time,
                    patients: [{ id: patientId, use_insurance: insurance }],
                    symptom_ids: [],
                    optimistic: true
                }
            ) || null;
            if(request && request.id) {
                if(outreachVisit && outreachVisit._id && outreachVisit.clientId && outreachVisit.patientId) {
                    const visitLocalTime = DateTime.utc(time.year, time.month, time.day, time.hour, time.minute, 0).setZone(officeZone);
                    let visitUpdate = { ...outreachVisit, ...{appointmentId:request.id}};
                    if(!outreachVisit.scheduled1?.trim() && !outreachVisit.scheduledReason1?.trim()) {
                        visitUpdate = { ...visitUpdate, ...{scheduled1:visitLocalTime.toISO()||undefined} }
                    } else if(!outreachVisit.scheduled2?.trim() && !outreachVisit.scheduledReason2?.trim()) {
                        visitUpdate = { ...visitUpdate, ...{scheduled2:visitLocalTime.toISO()||undefined} }
                    } else if(!outreachVisit.scheduled3?.trim() && !outreachVisit.scheduledReason3?.trim()) {
                        visitUpdate = { ...visitUpdate, ...{scheduled3:visitLocalTime.toISO()||undefined} }
                    } else if(!outreachVisit.scheduled4?.trim() && !outreachVisit.scheduledReason4?.trim()) {
                        visitUpdate = { ...visitUpdate, ...{scheduled4:visitLocalTime.toISO()||undefined} }
                    }
                    const reqUp = await API.updateVisit(visitUpdate).catch((error) => console.log(error));
                    if(reqUp && onUpdate) {
                        onUpdate({visits:[visitUpdate]});
                    }
                } else {
                    console.log('No Visit Provided to Assign Schedule');
                }
            }
            return (request && request.id) ? true : false;
        }catch(error) {
            console.error(error)
        }
        return false;
    }, [outreachVisit, onUpdate, officeZone]);

    return (
        <ResizableDialog {...props} open={open} onClose={(_e,_r) => { onCancel && onCancel(); }} canExpand={true} 
            title={'Book Appointment'}
            initialSettings={props?.PaperProps || { sx: { height:'80%', maxHeight:'90vh', minHeight:'70%', 
                width:'500px', minWidth:'500px', maxWidth: '80%' } }}
        >
            <DialogContent>
                <Typography variant='h5'>Patient</Typography>
                <Box sx={{ width: '1px', height: '0.5em' }} />
                <GridForm>
                <GridFormItem xs={6}>
                    <ZiphyPatientInput
                        patientId={outreachPatient.zcmId ? Number.parseInt(outreachPatient.zcmId) : undefined}
                        value={ziphyPatient}
                        practiceId={practiceId}
                        initialInput={`${outreachPatient.firstName} ${outreachPatient.lastName}`}
                        onChange={setZiphyPatient}/>

                </GridFormItem>
                <GridFormItem xs={6}>
                    <ZiphyPlaceInput
                        disabled={!ziphyPatient?.id}
                        value={place}
                        placeId={ziphyPatient?.place_id || undefined}
                        practiceId={practiceId}
                        initialAddress={getAddress(outreachPatient)}
                        onChange={setPlace}
                    />
                    <Typography variant='body2' sx={{ visibility: place == null ? 'visible' : 'hidden' }}>
                        No Place found?
                        You may <Link
                            component='button'
                            variant='body2'
                            onClick={showPlaceEditor}
                        >create</Link> one
                    </Typography>
                </GridFormItem>
                </GridForm>
                {
                    isPlaceEditorOpen && <AddressDialog mode={AddressDialogMode.CreatePlace} open={isPlaceEditorOpen}
                        practiceId={practiceId} patient={ziphyPatient || undefined} outreachPatient={outreachPatient}
                        onChange={setPlace} onClose={hidePlaceEditor}/>
                }
                <Box sx={{ width: '1px', height: '1em' }} />

                <Typography variant='h5'>Booking</Typography>
                <Box sx={{ width: '1px', height: '0.5em' }} />

                <GridForm>
                    <GridFormItem xs={6}>
                    <FormControl variant='standard' fullWidth >
                        <InputLabel>Service</InputLabel>
                        <Select name={'service'} value={serviceId} onChange={(e) => onEnumChange('service',e.target.value)}>
                        {
                            buildServiceSelectOptions.map((option, index) =>
                            option.type === "header" ? (
                                <MenuItem key={index} disabled><strong>{option.label}</strong></MenuItem>
                            ) : (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))
                        }
                        </Select>
                    </FormControl>
                    </GridFormItem>
                    <GridFormItem xs={2}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker
                            value={startDate}
                            onChange={onStartDateChange}
                            label='Start Date'
                            minDate={DateTime.now().toISODate()}
                            disableMaskedInput
                            renderInput={(params) =>
                                <TextField name='date' variant='standard' {...params} />
                            }
                        />
                    </LocalizationProvider>
                    </GridFormItem>
                    <GridFormItem xs={2}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePicker
                            value={endDate}
                            onChange={onEndDateChange}
                            disabled={true}
                            label='End Date'
                            disableMaskedInput
                            renderInput={(params) =>
                                <TextField name='date' variant='standard' {...params} />
                            }
                        />
                    </LocalizationProvider>
                    </GridFormItem>

                    <GridFormItem xs={2}>
                    <SpinnerButton fullWidth sx={{textAlign:'center'}}
                        disabled={  !office?.id || !officeZone || !startDate || !endDate || startDate >= endDate || !place?.id || !service || !practiceId }
                        onClick={async () => {
                            setSearchingSchedules(true);
                            if(officeZone && ziphyPatient && office?.id && startDate && endDate
                                    && place?.id && service && service?.service && practiceId) {
                                try {
                                await requestStaffSchedule(
                                    ziphyPatient,
                                    officeZone,
                                    luxonToDtime(startDate),
                                    {   year: endDate.year, month: endDate.month, day: endDate.day + 1,
                                        hour: 0, minute: 0, second: 0, utcoffset: 0, _type: 'datetime' },
                                        office.id, service.service, place?.id, 1);
                                }catch(error) {
                                    console.error(error);
                                }
                            }
                            setSearchingSchedules(false);
                        }
                        } showSpinner={searchingSchedules}>Search Slots</SpinnerButton>
                    </GridFormItem>
                </GridForm>

                <Box sx={{ width: '1px', height: '0.5em' }} />

                <Box
                    sx={{
                        padding: 1,
                        textAlign: 'center',
                        minHeight: '80px',
                        width:'100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                <Typography variant="body1">{'The schedule is only available for 2 consecutive days at a time. Blue times are available to book. Gray times are not available.'}</Typography>
                </Box>

                <CalendarGrid timeZone={officeZone} start={startDate} end={endDate} windows={scheduleWindows || []}
                    noWindowsText={ searchingSchedules ? 'Searching For Available Times' :
                        service ? 'Select a Service and press Search Slots to view available times.' : 'Press Search Slots to search for available times.'
                    }
                    interval={service?.duration || 60} onSelect={(slot) => {

                        const sll = scheduleStaff || [];
                        const staff:ScheduleStaffPair[] = [];
                        (slot?.staff || []).forEach((st) => {
                            const sm = sll.find(ck => ck.key == st);
                            if(sm) {
                                staff.push(sm);
                            }
                        });
                        const providerList = staff.filter(f => f?.value?.role == 'provider') || [];
                        const agentList = staff.filter(f => f?.value?.role == 'agent') || [];
                        const preferedIDs = ziphyPatient?.preferred_provider_ids || [];
                        const preferedProvider = slot.hasPrefered ? providerList.find((pl) => pl.key && preferedIDs.includes(pl.key)) : undefined;
                        setProviders(providerList);
                        setAgents(agentList);
                        setProviderId(preferedProvider?.key || (providerList.length > 0 ? providerList[0]?.key : undefined));
                        setAgentId(agentList.length > 0 ? agentList[0]?.key : undefined);
                        setSelectedWindow(slot);
                        setPatientNotes(createPatientNotes(patientTrackables?.data?.items||[], outreachPatient, outreachVisit));
                    }}/>

            </DialogContent>
            <DialogActions>
                <Button type='button' onClick={onCancel}>Close</Button>
            </DialogActions>

            { selectedWindow && office && service && <ConfirmBookingDialog 
                open={!!selectedWindow}
                place={place}
                useInsurance={useInsurance}
                duration={service?.duration || 0}
                bookingWindow={selectedWindow}
                office={office}
                serviceName={service?.service?.name}
                patient={outreachPatient}
                notes={patientNotes}
                practiceName={practiceQuery.data?.name || ''}
                preferedIds={ziphyPatient?.preferred_provider_ids||[]}
                providerId={providerId}
                agentId={agentId}
                providers={providers}
                agents={agents}
                onUpdateState={onConfirmDialogUpdateState}
                onCancel={()=> {setSelectedWindow(undefined)}}
                onBookClick={
                    async (pid:number, aid:number, insurance:boolean, notes:string)=>{
                        const status = await onBookClicked(Number.parseInt(outreachPatient.zcmId || ''), practiceId, office.id,
                            place?.id || 0, Number.parseInt(serviceId), aid || 0, pid || 0, 
                            notes, selectedWindow.time, insurance);
                        status ? showBookingSuccess() : showBookingError();
                        setSelectedWindow(undefined);
                    }
                }
            />}
            { isBookingSuccesOpen && <SimpleDialog open={isBookingSuccesOpen} title={'Booking Successful'}
                body={[
                    <Typography key='hc1' variant='body1'>{'Appointment was successfully booked for:'}</Typography>,
                    <Typography key='hc2' variant='body1'>{`${outreachPatient.firstName} ${outreachPatient.lastName}`}</Typography>
                ]}
                buttons={[
                    {text:'Close', onClick:hideBookingSuccess}
                ]}
            />}
            { isBookingErrorOpen && <SimpleDialog open={isBookingErrorOpen} title={'Booking Failed'}
                body={[
                    <Typography key='hc1' variant='body1'>{'Failed to book appointment for:'}</Typography>,
                    <Typography key='hc2' variant='body1'>{`${outreachPatient.firstName} ${outreachPatient.lastName}`}</Typography>
                ]}
                buttons={[
                    {text:'Close', onClick:hideBookingError}
                ]}
            />}
        </ResizableDialog>
    );
}

export default BookDialog;
